import { StoreModel } from './index'
import { action, Action, thunk, Thunk } from 'easy-peasy'
import { borrowerPortalRequest } from '@mortgage-pos/ui/services/http'
import incense from '@mortgage-pos/ui/incense'
import { isAusResultApproved } from '@mortgage-pos/utils'

const UNDERWRITING_REQUEST_FAILED_ERROR_DETAILS = 'Underwriting request failed'

export interface FannieModel {
  // State
  connectUrl: string | null
  connectToken: string | null
  connectUrlUsed: boolean
  isOrdering: boolean
  hasImported: boolean
  hasOrderedUnderwrite: boolean
  isAusIneligible: boolean
  hasUsableAusReport: boolean
  hasOrderedCredit: boolean
  orderCreditError: string | null

  // Actions
  setConnectUrl: Action<FannieModel, string>
  setConnectToken: Action<FannieModel, string | null>
  setIsOrdering: Action<FannieModel, boolean>

  setHasImported: Action<FannieModel, boolean>
  setHasOrderedUnderwrite: Action<FannieModel, boolean>
  setHasOrderedCredit: Action<FannieModel, boolean>
  setOrderCreditError: Action<FannieModel, string | null>
  setConnectUrlUsed: Action<FannieModel, boolean>
  setIsAusIneligible: Action<FannieModel, boolean>
  setHasUsableAusReport: Action<FannieModel, boolean>

  // Thunks
  importApplication: Thunk<FannieModel, null, object, StoreModel>
  getConnectUrl: Thunk<FannieModel, null, object, StoreModel>

  orderCredit: Thunk<
    FannieModel,
    { creditPullType: string },
    object,
    StoreModel,
    Promise<boolean>
  >

  orderReport: Thunk<FannieModel, null, object, StoreModel>

  orderUnderwrite: Thunk<FannieModel, null, object, StoreModel>
}

const fannie = (): FannieModel => {
  return {
    connectUrl: null,
    connectToken: null,
    connectUrlUsed: false,
    isOrdering: false,
    hasImported: false,
    hasOrderedUnderwrite: false,
    isAusIneligible: false,
    // Default true so uninitialized state doesn't hard-off-ramp.
    hasUsableAusReport: true,
    hasOrderedCredit: false,
    orderCreditError: null,

    setConnectUrl: action((state, payload) => {
      state.connectUrl = payload
    }),

    setConnectToken: action((state, payload) => {
      state.connectToken = payload
    }),

    setConnectUrlUsed: action((state, payload) => {
      state.connectUrlUsed = payload
    }),

    setIsOrdering: action((state, payload) => {
      state.isOrdering = payload
    }),

    setHasImported: action((state, payload) => {
      state.hasImported = payload
    }),

    setHasOrderedUnderwrite: action((state, payload) => {
      state.hasOrderedUnderwrite = payload
    }),

    setIsAusIneligible: action((state, payload) => {
      state.isAusIneligible = payload
    }),

    setHasUsableAusReport: action((state, payload) => {
      state.hasUsableAusReport = payload
    }),

    setHasOrderedCredit: action((state, payload) => {
      state.hasOrderedCredit = payload
    }),

    setOrderCreditError: action((state, payload) => {
      state.orderCreditError = payload
    }),

    importApplication: thunk(
      async (
        { setIsOrdering, setHasImported },
        _,
        { getStoreState, getState }
      ) => {
        const { applicationId } = getStoreState().application
        const { hasImported, isOrdering } = getState()

        if (hasImported || isOrdering) return true

        setIsOrdering(true)

        try {
          await borrowerPortalRequest(
            '/fannie/order',
            {
              options: {
                creditRequestType: 'Resubmit',
                functionType: 'Import',
                enableVapdPilot: false,
                isAutomationPilot: true,
              },
            },
            {},
            'post'
          )

          setHasImported(true)
          setIsOrdering(false)
          return true
        } catch (error) {
          setIsOrdering(false)
          incense(error)
            .details({
              name: 'FannieImportError',
              message: 'Failed to import application to Fannie Mae',
            })
            .safe({ applicationId })
            .error()

          throw error
        }
      }
    ),

    getConnectUrl: thunk(
      async (
        { setIsOrdering, setConnectUrl, setConnectToken, setConnectUrlUsed },
        _,
        { getStoreState, getStoreActions, getState }
      ) => {
        const { applicationId } = getStoreState().application
        const { connectUrl, connectUrlUsed, isOrdering } = getState()

        if ((!connectUrl || (connectUrl && connectUrlUsed)) && !isOrdering) {
          setIsOrdering(true)

          try {
            const response = await borrowerPortalRequest(
              '/fannie/order',
              {
                options: {
                  creditRequestType: 'Resubmit',
                  functionType: 'OrderReportConnect',
                  enableVapdPilot: false,
                  isAutomationPilot: true,
                  isSimpleV2:
                    getStoreState().questionnaire.isSimpleV2FannieFlow,
                },
              },
              {},
              'post'
            )

            if (response.data.connectToken) {
              setConnectToken(response.data.connectToken)
              // Set on plaid store so PlaidLink can use it directly
              getStoreActions().plaid.setLinkToken(response.data.connectToken)
            }

            if (response.data.connectUrl) {
              setConnectUrl(response?.data?.connectUrl)
              setConnectUrlUsed(false)
            }

            setIsOrdering(false)
            return true
          } catch (error) {
            setIsOrdering(false)
            incense(error)
              .details({
                name: 'FannieOrderReportConnectError',
                message:
                  'Failed to get Fannie Mae connect URL via OrderReportConnect',
              })
              .safe({ applicationId })
              .error()

            throw error
          }
        }

        return true
      }
    ),

    orderCredit: thunk(
      async (
        { setIsOrdering, setHasOrderedCredit, setOrderCreditError },
        payload,
        { getStoreState, getState }
      ) => {
        const { applicationId } = getStoreState().application
        const { isOrdering, hasOrderedCredit } = getState()

        // Store-level dedupe: guards against React StrictMode double-mount
        // (and any other duplicate trigger) so only one request actually
        // fires per application/session.
        if (isOrdering || hasOrderedCredit) return true

        setOrderCreditError(null)
        setIsOrdering(true)

        try {
          const response = await borrowerPortalRequest(
            '/fannie/order-credit',
            {
              creditPullType: payload.creditPullType,
            },
            {},
            'post'
          )

          const success = Boolean(response?.data?.success)
          if (!success) setOrderCreditError('credit_pull_failed')
          setHasOrderedCredit(true)
          setIsOrdering(false)
          return success
        } catch (error) {
          setIsOrdering(false)
          const message =
            error instanceof Error
              ? error.message
              : 'Failed to order Fannie Mae credit'
          setOrderCreditError(message)
          incense(error)
            .details({
              name: 'FannieOrderCreditError',
              message: 'Failed to order Fannie Mae credit',
            })
            .safe({ applicationId })
            .error()
          throw error
        }
      }
    ),

    orderReport: thunk(async ({ setIsOrdering }, _, { getStoreState }) => {
      const { applicationId } = getStoreState().application
      const { isFannieFlow, isSimpleV2FannieFlow } =
        getStoreState().questionnaire

      try {
        await borrowerPortalRequest(
          '/fannie/order',
          {
            options: {
              creditRequestType: 'Reissue',
              functionType: 'OrderReportVOD',
              enableVapdPilot: false,
              isAutomationPilot: isFannieFlow || isSimpleV2FannieFlow,
              isSimpleV2: isSimpleV2FannieFlow,
            },
          },
          {},
          'post'
        )

        return true
      } catch (error) {
        incense(error)
          .details({
            name: 'FannieOrderReportError',
            message: 'Failed to order Fannie Mae VOD report',
          })
          .safe({ applicationId })
          .error()

        // swallow error
        // throw error
      }
    }),

    orderUnderwrite: thunk(
      async (
        {
          setIsOrdering,
          setHasOrderedUnderwrite,
          setIsAusIneligible,
          setHasUsableAusReport,
        },
        _,
        { getStoreState, getState, getStoreActions }
      ) => {
        const { isOrdering } = getState()
        const { application, questionnaire, credit } = getStoreState()
        const { applicationId } = application
        const { isFannieFlow, isSimpleV2Flow } = questionnaire
        const { elementLoaded, formErrored } = getStoreActions().tracking

        if (isOrdering) return

        if (credit.creditHold) {
          setHasUsableAusReport(true)
          setIsAusIneligible(false)
          setHasOrderedUnderwrite(true)
          return
        }

        setIsOrdering(true)
        try {
          const response = await borrowerPortalRequest(
            '/fannie/order',
            {
              options: {
                creditRequestType: isFannieFlow ? 'Request' : 'Reissue',
                functionType: isFannieFlow
                  ? 'UnderwriteWithReport'
                  : 'Underwrite',
                enableVapdPilot: false,
                isAutomationPilot: isFannieFlow,
                isSimpleV2: isSimpleV2Flow,
              },
            },
            {},
            'post'
          )

          const receivedReport = Boolean(response?.data?.receivedReport)
          const duResultString = response?.data?.result?.result as
            | string
            | undefined

          setHasUsableAusReport(receivedReport)
          setIsAusIneligible(
            receivedReport &&
              !!duResultString &&
              !isAusResultApproved(duResultString)
          )
          setIsOrdering(false)
          setHasOrderedUnderwrite(true)
          if (isSimpleV2Flow) {
            elementLoaded({
              elementName: 'underwriting_request_completed',
              location: 'simple_v2_lifecycle',
            })
          }
          return true
        } catch (error) {
          incense(error)
            .details({
              name: 'FannieOrderReportError',
              message: 'Failed to order Fannie Mae Direct DU report',
            })
            .safe({
              applicationId,
            })
            .error()

          setIsOrdering(false)
          // Keep the borrower in the flow on transient failures — a missing
          // AUS result should not be treated as a definitive no_viable_products.
          setHasUsableAusReport(true)
          setIsAusIneligible(false)
          setHasOrderedUnderwrite(true)
          if (isSimpleV2Flow) {
            formErrored({
              errorType: 'underwriting_request_failed',
              errorMessage: UNDERWRITING_REQUEST_FAILED_ERROR_DETAILS,
              errorDetails: UNDERWRITING_REQUEST_FAILED_ERROR_DETAILS,
            })
          }
          // swallow error
          // throw error
        }
      }
    ),
  }
}

export default fannie
